import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import apiAxios from "helpers/apiAxios";
import { toast } from "react-toastify";
import { objectSerializer } from "helpers/utils";
import {
  initialState,
  handleFulfilled,
  setNestedPageHelper,
  setNestedPageSizeHelper,
  setNestedParamsHelper,
  resetNestedParamsHelper,
  setSortParamsHelper,
  setFilterTagHelper,
  resetFilterTagHelper
} from "helpers/reducerHelpers";

const extractionUrl = "/document/extractions";
const promptUrl     = "/document/extraction_prompts";

// List (admin sees all; server-side grouped-by-file + filter/sort/pagination).
export const getExtractions = createAsyncThunk(
  "DocExtraction/getExtractions",
  async (params) => {
    try {
      const response = await apiAxios.get(extractionUrl, { params });
      return response.data;
    } catch (error) {
      throw new Error(error.message);
    }
  }
);

// Re-run a failed/completed extraction (reset + re-enqueue on the backend).
export const retryExtraction = createAsyncThunk(
  "DocExtraction/retryExtraction",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${extractionUrl}/${id}/re_extract`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

export const cancelExtraction = createAsyncThunk(
  "DocExtraction/cancelExtraction",
  async (id, { rejectWithValue }) => {
    try {
      const response = await apiAxios.post(`${extractionUrl}/${id}/cancel`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

export const getExtractionPrompts = createAsyncThunk(
  "DocExtraction/getExtractionPrompts",
  async () => {
    try {
      const response = await apiAxios.get(promptUrl);
      return response.data;
    } catch (error) {
      throw new Error(error.message);
    }
  }
);

export const updateExtractionPrompt = createAsyncThunk(
  "DocExtraction/updateExtractionPrompt",
  async ({ id, data }, { rejectWithValue }) => {
    try {
      const response = await apiAxios.put(`${promptUrl}/${id}`, data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: error.message });
    }
  }
);

const isRejectedAction = (action) => (action.type.startsWith("DocExtraction/")) && action.type.endsWith("/rejected");

const extractionAdminSlice = createSlice({
  name: "admin_document_extraction",
  initialState: {
    ...initialState,
    prompts: []
  },
  reducers: {
    setNestedPage: setNestedPageHelper,
    setNestedPageSize: setNestedPageSizeHelper,
    setNestedParams: setNestedParamsHelper,
    resetNestedParams: resetNestedParamsHelper,
    setSortParams: setSortParamsHelper,
    setFilterTag: setFilterTagHelper,
    resetFilterTag: resetFilterTagHelper
  },
  extraReducers: (builder) => {
    builder
      .addCase(getExtractions.fulfilled, (state, action) => handleFulfilled(state, action, "allData"))
      .addCase(retryExtraction.fulfilled, (state) => {
        state.loading = false;
        toast.success("Extraction re-queued.");
      })
      .addCase(cancelExtraction.fulfilled, (state) => {
        state.loading = false;
        toast.success("Extraction canceled.");
      })
      .addCase(cancelExtraction.rejected, (state, action) => {
        state.loading = false;
        toast.error(action.payload?.error || "Failed to cancel extraction.");
      })
      .addCase(getExtractionPrompts.fulfilled, (state, action) => {
        state.prompts = objectSerializer(action.payload?.rows || []) || [];
      })
      .addCase(updateExtractionPrompt.fulfilled, (state) => {
        state.loading = false;
        toast.success("Prompt configuration saved.");
      })
      .addMatcher(isRejectedAction, (state, action) => {
        state.loading = false;
        toast.error(action.payload?.error || action?.error?.message);
      });
  }
});

export const {
  setNestedPage,
  setNestedPageSize,
  setNestedParams,
  resetNestedParams,
  setSortParams,
  setFilterTag,
  resetFilterTag
} = extractionAdminSlice.actions;

export default extractionAdminSlice.reducer;
